import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getChallan, getChallanPdfUrl } from '../lib/api';
import { ArrowLeft, FilePdf, Printer } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';

const statusConfig = {
    draft: { label: 'Draft', variant: 'secondary' },
    issued: { label: 'Issued', variant: 'default' },
    closed: { label: 'Closed', variant: 'outline' },
    cancelled: { label: 'Cancelled', variant: 'destructive' },
};

export default function ChallanPreview() {
    const { challanId } = useParams();
    const [challan, setChallan] = useState(null);
    const [lines, setLines] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await getChallan(challanId);
                setChallan(res.data.challan);
                setLines(res.data.lines || []);
            } catch { /* ignore */ }
            finally { setLoading(false); }
        })();
    }, [challanId]);

    if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-[600px]" /></div>;
    if (!challan) return <div className="text-center py-20 text-muted-foreground">Challan not found</div>;

    const sc = statusConfig[challan.status] || {};
    const totalQty = lines.reduce((s, l) => s + (l.quantity || 0), 0);

    return (
        <div className="space-y-4" data-testid="challan-preview">
            {/* Actions bar - hidden on print */}
            <div className="flex items-center justify-between print:hidden">
                <Button variant="ghost" size="sm" asChild className="-ml-2">
                    <Link to={`/challans/${challanId}`}><ArrowLeft size={16} className="mr-1" />Edit</Link>
                </Button>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-btn">
                        <Printer size={16} className="mr-2" />Print
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => window.open(getChallanPdfUrl(challanId), '_blank')} data-testid="pdf-btn">
                        <FilePdf size={16} className="mr-2" />Export PDF
                    </Button>
                </div>
            </div>

            {/* Printable challan */}
            <div className="bg-white border border-border rounded-lg max-w-[800px] mx-auto shadow-sm print:shadow-none print:border-0 print:max-w-none" data-testid="challan-document">
                <div className="p-8">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-6 pb-4 border-b-2 border-[#C8A064]">
                        <div>
                            <h1 className="text-xl font-bold text-[#3C3223] tracking-wide">DELIVERY CHALLAN</h1>
                            <p className="text-lg font-mono font-bold mt-1 text-[#C8A064]">{challan.challan_number}</p>
                        </div>
                        <div className="text-right text-sm">
                            <p className="font-semibold text-[#3C3223]">Lamikraft</p>
                            <p className="text-muted-foreground">Crown Decor Pvt. Ltd.</p>
                            <p className="text-xs text-muted-foreground mt-1">Date: {challan.challan_date}</p>
                            <Badge variant={sc.variant} className="mt-1 text-[10px]">{sc.label || challan.status}</Badge>
                        </div>
                    </div>

                    {/* Info Grid */}
                    <div className="grid grid-cols-2 gap-6 mb-6">
                        <div className="space-y-1.5 text-sm">
                            <h3 className="text-xs font-semibold text-muted-foreground uppercase">From</h3>
                            <p className="font-medium">{challan.source_location || 'Crown / Lamikraft Office'}</p>
                            <p className="text-muted-foreground text-xs">Crown Decor Pvt. Ltd.</p>
                        </div>
                        <div className="space-y-1.5 text-sm">
                            <h3 className="text-xs font-semibold text-muted-foreground uppercase">To</h3>
                            <p className="font-medium">{challan.destination_party || '—'}</p>
                            {challan.shipping_name && <p className="text-xs">{challan.shipping_name}</p>}
                            {challan.address && <p className="text-xs text-muted-foreground">{challan.address}</p>}
                            <div className="text-xs text-muted-foreground space-x-3">
                                {challan.gstin && <span>GSTIN: {challan.gstin}</span>}
                                {challan.phone && <span>Ph: {challan.phone}</span>}
                                {challan.email && <span>{challan.email}</span>}
                            </div>
                        </div>
                    </div>

                    {/* Meta row */}
                    <div className="flex flex-wrap gap-4 mb-6 text-xs">
                        <div className="bg-muted/30 px-3 py-1.5 rounded">
                            <span className="text-muted-foreground">Type:</span> <span className="font-medium">{challan.challan_type}</span>
                        </div>
                        <div className="bg-muted/30 px-3 py-1.5 rounded">
                            <span className="text-muted-foreground">Return:</span> <span className="font-medium capitalize">{challan.return_type}</span>
                        </div>
                        <div className="bg-muted/30 px-3 py-1.5 rounded">
                            <span className="text-muted-foreground">Dest. Type:</span> <span className="font-medium">{challan.destination_type}</span>
                        </div>
                        {challan.reference_no && (
                            <div className="bg-muted/30 px-3 py-1.5 rounded">
                                <span className="text-muted-foreground">Ref:</span> <span className="font-medium">{challan.reference_no}</span>
                            </div>
                        )}
                    </div>

                    {/* Items Table */}
                    <table className="w-full border-collapse text-sm mb-6">
                        <thead>
                            <tr className="bg-[#C8A064] text-white">
                                <th className="px-3 py-2 text-left text-xs font-semibold w-8">#</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold">Code</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold">Description</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold">HSN/SAC</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold">Qty</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold">Unit</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold">Remarks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lines.map((line, idx) => (
                                <tr key={line.id} className={idx % 2 === 0 ? 'bg-stone-50' : 'bg-white'}>
                                    <td className="px-3 py-2 text-xs text-muted-foreground">{idx + 1}</td>
                                    <td className="px-3 py-2 font-mono text-xs">{line.item_code || '—'}</td>
                                    <td className="px-3 py-2 text-xs">{line.item_name}</td>
                                    <td className="px-3 py-2 text-xs text-muted-foreground">{line.hsn_code || '—'}</td>
                                    <td className="px-3 py-2 text-right font-mono text-xs font-semibold">{line.quantity}</td>
                                    <td className="px-3 py-2 text-xs">{line.unit}</td>
                                    <td className="px-3 py-2 text-xs text-muted-foreground">{line.remarks || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="border-t-2 border-[#C8A064]">
                                <td colSpan={4} className="px-3 py-2 text-right font-semibold text-xs">Total Quantity:</td>
                                <td className="px-3 py-2 text-right font-mono font-bold text-sm">{totalQty}</td>
                                <td colSpan={2}></td>
                            </tr>
                        </tfoot>
                    </table>

                    {/* Remarks */}
                    {challan.remarks && (
                        <div className="mb-6 text-xs">
                            <p className="font-semibold text-muted-foreground mb-1">Remarks:</p>
                            <p className="text-muted-foreground italic">{challan.remarks}</p>
                        </div>
                    )}

                    {/* Terms */}
                    <div className="mb-8 text-[10px] text-muted-foreground border-t border-border pt-3">
                        <p className="font-semibold text-xs mb-1">Terms & Conditions:</p>
                        <ol className="list-decimal list-inside space-y-0.5">
                            <li>Goods are being sent on delivery challan basis and are not for sale.</li>
                            <li>Please return the signed copy of this challan.</li>
                            <li>Any damage/shortage must be reported immediately on receipt.</li>
                            {challan.return_type === 'returnable' && (
                                <li>Materials are returnable and must be returned in original condition.</li>
                            )}
                        </ol>
                    </div>

                    {/* Signatures */}
                    <div className="grid grid-cols-3 gap-8 pt-8 border-t border-border">
                        <div className="text-center">
                            <div className="border-b border-muted-foreground/30 pb-8 mb-2"></div>
                            <p className="text-xs font-semibold">Delivered By</p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">Name & Date</p>
                        </div>
                        <div className="text-center">
                            <div className="border-b border-muted-foreground/30 pb-8 mb-2"></div>
                            <p className="text-xs font-semibold">Received By</p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">Name, Date & Stamp</p>
                        </div>
                        <div className="text-center">
                            <div className="border-b border-muted-foreground/30 pb-8 mb-2"></div>
                            <p className="text-xs font-semibold">Authorized Signatory</p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">Crown Decor Pvt. Ltd.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
